<template>
  <div>
    <div 
      class="sidebar-backdrop" 
      :class="{ 'is-active': $sidebar.showSidebar }" 
      @click="closeSidebarOnMobile"
    ></div>

    <div 
      class="app-sidebar" 
      :class="{ 'is-collapsed': !$sidebar.showSidebar, 'is-hovered': isHovered }"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      
      <div class="sidebar-header">
        <a href="http://www.lirs.gov.ng" target="_blank" class="logo-container">
          <img src="/img/home/lirs-logo.png" alt="Logo" class="logo-img" />
          <span class="logo-text" :class="{ 'text-visible': $sidebar.showSidebar || isHovered }">
            {{ title }}
          </span>
        </a>
        
        <div class="toggle-btn" @click="toggleSidebar">
          <sidebar-toggle-icon 
            :class="{ 'is-expanded': $sidebar.showSidebar && !isHovered }" />
        </div>
      </div>

      <div class="sidebar-wrapper" ref="sidebarScrollArea">
        <slot></slot>
        
        <ul class="nav-list">
          <slot name="links" v-bind="{ isHovered, showSidebar: $sidebar.showSidebar }">
            <sidebar-item 
              v-for="(link, index) in sidebarLinks" 
              :key="link.name + index" 
              :link="link"
            >
              <sidebar-item
                v-for="(subLink, subIndex) in link.children"
                :key="subLink.name + subIndex"
                :link="subLink"
              ></sidebar-item>
            </sidebar-item>
          </slot>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import SidebarToggleIcon from '../Icons/SidebarIcons/sidebar-toggle-icon.vue';

export default {
  name: "sidebar",
  components: {
    SidebarToggleIcon
  },
  props: {
    title: {
      type: String,
      default: "E-TAX"
    },
    sidebarLinks: {
      type: Array,
      default: () => []
    },
    autoClose: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      isHovered: false
    };
  },
  computed: {
    collapsed() {
      return !this.$sidebar.showSidebar && !this.isHovered;
    }
  },
  provide() {
    return {
      autoClose: this.autoClose,
      sidebarContext: this
    };
  },
  methods: {
    handleMouseEnter() {
      if (!this.$sidebar.showSidebar && window.innerWidth > 991) {
        this.isHovered = true;
      }
    },
    handleMouseLeave() {
      this.isHovered = false;
    },
    toggleSidebar() {
      const nextState = !this.$sidebar.showSidebar;
      this.$sidebar.displaySidebar(nextState);
      this.$emit("toggle", !nextState);
      
      if (typeof this.$sidebar.toggleMinimize === 'function') {
        this.$sidebar.toggleMinimize();
      }

      setTimeout(() => {
        if (nextState) {
          this.isHovered = false;
        }
      }, 50);
    },
    closeSidebarOnMobile() {
      this.isHovered = false;
      this.$sidebar.displaySidebar(false);
      this.$emit("toggle", true);
    }
  },
  mounted() {
    this.$router.afterEach(() => {
      if (window.innerWidth <= 991) {
        this.closeSidebarOnMobile();
      }
    });
  }
};
</script>

<style scoped>
.app-sidebar {
  width: 287px;
  height: 100vh;
  background-color: var(--nav-bg);
  display: flex;
  flex-direction: column;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease;
  overflow-x: hidden;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  box-sizing: border-box;
  position: relative;
  flex-shrink: 0;
  z-index: 1040;
}

.app-sidebar.is-collapsed {
  width: 80px; 
}

@media (min-width: 992px) {
  .app-sidebar.is-collapsed.is-hovered {
    width: 287px;
    box-shadow: 4px 0 15px rgba(0, 0, 0, 0.1);
  }
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 16px;
  min-height: 75px;
  box-sizing: border-box;
}

.logo-container {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: 7px;
}

.logo-img {
  width: 32px;
  height: 32px;
  object-fit: contain;
  flex-shrink: 0;
}

.logo-text {
  font-weight: 700;
  color: #ffffff;
  font-size: 15px;
  letter-spacing: 0.5px;
  white-space: nowrap;
  opacity: 0;
  max-width: 0;
  overflow: hidden;
  display: inline-block;
  transition: opacity 0.2s ease, max-width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.logo-text.text-visible {
  opacity: 1;
  max-width: 180px;
}

.toggle-btn {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;
}

.app-sidebar.is-collapsed:not(.is-hovered) .toggle-btn {
  opacity: 0;
  pointer-events: none;
}

.sidebar-wrapper {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0 12px 24px 12px;
}

.nav-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
}

.sidebar-wrapper::-webkit-scrollbar {
  width: 4px;
}
.sidebar-wrapper::-webkit-scrollbar-thumb {
  background: var(--nav-scroll-thumb-bg);
  border-radius: 4px;
}

.sidebar-backdrop {
  display: none;
}

.app-sidebar:not(.is-collapsed) {
  box-shadow: 4px 0px 40px 10px #0000002E;
}

@media (min-width: 992px) {
  .app-sidebar.is-collapsed.is-hovered {
    width: 287px;
    box-shadow: 4px 0px 40px 10px #0000002E;
  }
}


@media (max-width: 991px) {
  .sidebar-backdrop {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.4);
    z-index: 1030;
    
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .sidebar-backdrop.is-active {
    opacity: 1;
    pointer-events: auto;
  }
  .app-sidebar {
    position: fixed;
    top: 0;
    left: 0;
    width: 287px !important;
    transform: translate3d(0, 0, 0);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
    z-index: 1040;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .app-sidebar.is-collapsed {
    transform: translate3d(-287px, 0, 0) !important;
    width: 287px !important;
  }
  
  .app-sidebar.is-collapsed .toggle-btn {
    opacity: 1 !important;
    pointer-events: auto !important;
  }
}

.toggle-btn :deep(svg) {
  transform-origin: 50% 46%;
  transition: transform 0.3s ease;
}

.toggle-btn .is-expanded {
  transform: rotate(180deg);
}
</style>